'use strict';

/**
 * Camada de mapa de calor desenhada em canvas sobre o Google Maps.
 *
 * Substitui google.maps.visualization.HeatmapLayer, que o Google removeu a
 * partir da versao 3.65 da Maps JavaScript API. Usa apenas
 * google.maps.OverlayView, que continua suportado, portanto nao exige
 * dependencia externa nem a library 'visualization'.
 *
 * O canvas e inserido no pane 'overlayLayer', que fica ABAIXO dos marcadores:
 * os pinos continuam visiveis e clicaveis sobre o mapa de calor.
 *
 * Uso:
 *   const layer = heatMapOverlayFactory.create({
 *     map:    map,
 *     data:   [{ location: new google.maps.LatLng(lat, lng), weight: 3 }],
 *     radius: 25
 *   });
 *   layer.setData(outrosPontos);
 *   layer.setMap(null); // remove a camada
 */

// Mesma paleta padrao usada pelo HeatmapLayer removido, para manter o visual.
const DEFAULT_GRADIENT = [
  'rgba(0, 255, 255, 0)',
  'rgba(0, 255, 255, 1)',
  'rgba(0, 191, 255, 1)',
  'rgba(0, 127, 255, 1)',
  'rgba(0, 63, 255, 1)',
  'rgba(0, 0, 255, 1)',
  'rgba(0, 0, 223, 1)',
  'rgba(0, 0, 191, 1)',
  'rgba(0, 0, 159, 1)',
  'rgba(0, 0, 127, 1)',
  'rgba(63, 0, 91, 1)',
  'rgba(87, 0, 63, 1)',
  'rgba(111, 0, 47, 1)',
  'rgba(255, 0, 0, 1)'
];

const DEFAULT_RADIUS = 25;
const DEFAULT_OPACITY = 0.7;
// Alfa maximo aplicado a UM heatpoint. Mantido baixo de proposito: o alfa se
// acumula por sobreposicao (source-over), entao a cor quente aparece onde ha
// densidade, e nao onde ha um unico ponto de peso alto.
const POINT_ALPHA_SCALE = 0.12;
// Gamma < 1 empurra as densidades intermediarias para a parte quente da
// paleta. Sem isso quase toda a imagem cai na faixa azul, porque o pico de
// densidade fica muito acima da media.
const INTENSITY_GAMMA = 0.55;
const MIN_POINT_ALPHA = 0.015;
const MAX_CANVAS_SIDE = 4096;

/**
 * Rampa de 256 cores (RGBA) usada para colorir o canvas em funcao do alfa
 * acumulado de cada pixel.
 */
function buildGradientRamp(colors) {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 256;
  const context = canvas.getContext('2d');
  const gradient = context.createLinearGradient(0, 0, 0, 256);
  const lastIndex = Math.max(colors.length - 1, 1);
  colors.forEach(function(color, index) {
    gradient.addColorStop(index / lastIndex, color);
  });
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1, 256);
  return context.getImageData(0, 0, 1, 256).data;
}

/**
 * Pincel reaproveitado para cada heatpoint: circulo preto com queda radial de
 * opacidade. A cor final vem da rampa; aqui so interessa o canal alfa.
 */
function buildPointBrush(radius) {
  const canvas = document.createElement('canvas');
  const size = radius * 2;
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  const gradient = context.createRadialGradient(radius, radius, 0, radius, radius, radius);
  gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);
  return canvas;
}

function normalizePoints(data) {
  const points = [];
  (data || []).forEach(function(point) {
    if (!point || !point.location) {
      return;
    }
    const weight = Number(point.weight);
    points.push({
      location: point.location,
      weight:   (isFinite(weight) && weight > 0) ? weight : 1
    });
  });
  return points;
}

function maxWeightOf(points) {
  let max = 1;
  points.forEach(function(point) {
    if (point.weight > max) {
      max = point.weight;
    }
  });
  return max;
}

function heatMapOverlayFactory() {

  // A classe so pode ser definida depois que a Maps API carregou, porque herda
  // de google.maps.OverlayView.
  let HeatMapOverlay = null;

  function defineOverlayClass() {
    if (HeatMapOverlay) {
      return HeatMapOverlay;
    }

    HeatMapOverlay = function(options) {
      const settings = options || {};
      this.radius = settings.radius > 0 ? settings.radius : DEFAULT_RADIUS;
      this.opacity = (typeof settings.opacity === 'number') ? settings.opacity : DEFAULT_OPACITY;
      this.points = normalizePoints(settings.data);
      this.maxWeight = maxWeightOf(this.points);
      this.ramp = buildGradientRamp(settings.gradient || DEFAULT_GRADIENT);
      this.brush = buildPointBrush(this.radius);
      this.canvas = null;
      if (settings.map) {
        this.setMap(settings.map);
      }
    };

    HeatMapOverlay.prototype = new google.maps.OverlayView();

    HeatMapOverlay.prototype.onAdd = function() {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.opacity = this.opacity;
      this.canvas = canvas;
      this.getPanes().overlayLayer.appendChild(canvas);
    };

    HeatMapOverlay.prototype.onRemove = function() {
      if (this.canvas && this.canvas.parentNode) {
        this.canvas.parentNode.removeChild(this.canvas);
      }
      this.canvas = null;
    };

    HeatMapOverlay.prototype.draw = function() {
      const map = this.getMap();
      const projection = this.getProjection();
      if (!this.canvas || !map || !projection) {
        return;
      }
      const bounds = map.getBounds();
      if (!bounds) {
        return;
      }

      // Canvas cobre o viewport atual com uma folga de um raio de cada lado,
      // para que pontos logo fora da tela ainda contribuam com a densidade.
      const southWest = projection.fromLatLngToDivPixel(bounds.getSouthWest());
      const northEast = projection.fromLatLngToDivPixel(bounds.getNorthEast());
      const padding = this.radius * 2;
      const mapDiv = map.getDiv();

      let width = Math.ceil(northEast.x - southWest.x) + padding * 2;
      let height = Math.ceil(southWest.y - northEast.y) + padding * 2;
      if (!(width > 0) || width > MAX_CANVAS_SIDE) {
        width = mapDiv.offsetWidth + padding * 2;
      }
      if (!(height > 0) || height > MAX_CANVAS_SIDE) {
        height = mapDiv.offsetHeight + padding * 2;
      }

      const offsetX = Math.floor(southWest.x) - padding;
      const offsetY = Math.floor(northEast.y) - padding;

      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.left = offsetX + 'px';
      this.canvas.style.top = offsetY + 'px';
      this.canvas.style.width = width + 'px';
      this.canvas.style.height = height + 'px';

      this.render(width, height, offsetX, offsetY);
    };

    HeatMapOverlay.prototype.render = function(width, height, offsetX, offsetY) {
      const context = this.canvas.getContext('2d');
      context.clearRect(0, 0, width, height);
      if (!this.points.length) {
        return;
      }

      const projection = this.getProjection();
      const radius = this.radius;
      const maxWeight = this.maxWeight;
      const brush = this.brush;
      let drawn = 0;

      this.points.forEach(function(point) {
        const pixel = projection.fromLatLngToDivPixel(point.location);
        const x = pixel.x - offsetX;
        const y = pixel.y - offsetY;
        if (x < -radius || y < -radius || x > width + radius || y > height + radius) {
          return;
        }
        const alpha = (point.weight / maxWeight) * POINT_ALPHA_SCALE;
        context.globalAlpha = Math.min(Math.max(alpha, MIN_POINT_ALPHA), 1);
        context.drawImage(brush, x - radius, y - radius);
        drawn++;
      });
      context.globalAlpha = 1;

      if (!drawn) {
        return;
      }
      this.colorize(context, width, height);
    };

    /**
     * Troca o cinza acumulado pela cor correspondente na rampa, usando o canal
     * alfa de cada pixel como indice.
     */
    HeatMapOverlay.prototype.colorize = function(context, width, height) {
      const image = context.getImageData(0, 0, width, height);
      const pixels = image.data;
      const ramp = this.ramp;

      // Normaliza pelo pico de densidade visivel, como fazia o maxIntensity
      // automatico do HeatmapLayer: a regiao mais densa da tela chega ao topo
      // da paleta, independente da quantidade de pontos.
      let peak = 0;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] > peak) {
          peak = pixels[i];
        }
      }
      if (!peak) {
        return;
      }
      // Tabela alfa -> nivel normalizado (0..255), montada uma vez por quadro.
      const levels = new Uint8ClampedArray(256);
      for (let value = 1; value <= 255; value++) {
        const normalized = Math.min(value / peak, 1);
        levels[value] = Math.round(255 * Math.pow(normalized, INTENSITY_GAMMA));
      }

      for (let i = 3; i < pixels.length; i += 4) {
        const alpha = pixels[i];
        if (!alpha) {
          continue;
        }
        const level = levels[alpha];
        const rampIndex = level * 4;
        pixels[i - 3] = ramp[rampIndex];
        pixels[i - 2] = ramp[rampIndex + 1];
        pixels[i - 1] = ramp[rampIndex + 2];
        // O proprio nivel normalizado vira a opacidade: transicao suave do
        // transparente (baixa densidade) ao opaco (nucleo quente).
        pixels[i] = level;
      }
      context.putImageData(image, 0, 0);
    };

    HeatMapOverlay.prototype.setData = function(data) {
      this.points = normalizePoints(data);
      this.maxWeight = maxWeightOf(this.points);
      this.draw();
    };

    HeatMapOverlay.prototype.setRadius = function(radius) {
      if (!(radius > 0)) {
        return;
      }
      this.radius = radius;
      this.brush = buildPointBrush(radius);
      this.draw();
    };

    HeatMapOverlay.prototype.setOpacity = function(opacity) {
      this.opacity = opacity;
      if (this.canvas) {
        this.canvas.style.opacity = opacity;
      }
    };

    return HeatMapOverlay;
  }

  return {
    create: function(options) {
      const OverlayClass = defineOverlayClass();
      return new OverlayClass(options);
    }
  };
}

heatMapOverlayFactory.$inject = []
export default heatMapOverlayFactory



// WEBPACK FOOTER //
// ./src/app/scripts/factories/heatMapOverlayFactory.js